import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { PresentationalAtomsModule } from '../presentational-atoms.module';
import { DocumentProcessApiService } from '@et/api';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  catchError,
  debounceTime,
  filter,
  finalize,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { ConnectedPosition, Overlay } from '@angular/cdk/overlay';
import { TemplatesSearchListComponent } from '../templates-search-list/templates-search-list.component';
import { DocPrepDocument } from '@et/typings';
import { NotificationService } from '@et/notifications';
import { RouterLink } from '@angular/router';
import { PluralPipe } from '@et/shared';

@Component({
  selector: 'et-atoms-templates-search',
  imports: [
    PresentationalAtomsModule,
    ReactiveFormsModule,
    TemplatesSearchListComponent,
    NgTemplateOutlet,
    RouterLink,
    PluralPipe,
  ],
  templateUrl: './templates-search.component.html',
  styleUrl: './templates-search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: TemplatesSearchComponent,
      multi: true,
    },
  ],
})
export class TemplatesSearchComponent
  implements ControlValueAccessor, OnDestroy
{
  private autotagService = inject(DocumentProcessApiService);
  private overlay = inject(Overlay);
  private notificationService = inject(NotificationService);

  private onChange!: (value: DocPrepDocument | undefined) => void;
  private onTouched!: () => void;
  protected disabled = false;

  private cancelRequest$ = new Subject<void>();
  private continuationToken: string | null = null;
  private currentSearchTerm = '';

  @Input({ required: true }) companyId!: string;
  @ViewChild('inputEl') inputEl: ElementRef<HTMLInputElement> | undefined;

  template = signal<DocPrepDocument | undefined>(undefined);
  templates = signal<DocPrepDocument[]>([]);
  loading = signal(false);
  hasMore = signal(false);

  searchCtrl = new FormControl('');

  isSearchMenuOpen = signal<boolean>(false);

  constructor() {
    this.setupSearchSubscription();
  }

  position: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 1,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -1,
    },
  ];
  scrollStrategy = this.overlay.scrollStrategies.block();

  /**
   * Handles the selection of a template.
   *
   * This method sets the search control value to the selected template's name, closes the search menu,
   * emits the selected template, and blurs the input element.
   *
   * @param {DocPrepDocument} template - The selected template.
   */
  onTemplateSelected(template: DocPrepDocument) {
    this.template.set(template);
    this.isSearchMenuOpen.set(false);
    this.inputEl?.nativeElement.blur();
    this.onChange(template);
    this.onTouched();
  }

  /**
   * Handles the deletion of the selected template.
   *
   * This method emits the `templateDeleted` event and resets the search control.
   */
  onDeleteSelectedTemplate() {
    this.searchCtrl.reset();
    this.template.set(undefined);
    this.onChange(undefined);
    this.onTouched();
    setTimeout(() => this.inputEl?.nativeElement.focus());
  }

  /**
   * Writes the value to the component.
   *
   * This method sets the provided template value to the component.
   *
   * @param {DocPrepDocument} value - The template value to be written.
   * @returns {void}
   */
  writeValue(value: DocPrepDocument): void {
    this.template.set(value);
  }

  /**
   * Registers a callback function to be called when the value changes.
   *
   * This method sets the `onChange` callback function.
   *
   * @param {(value: DocPrepDocument | undefined) => void} fn - The callback function to be registered.
   * @returns {void}
   */
  registerOnChange(fn: (_: DocPrepDocument | undefined) => void): void {
    this.onChange = fn;
  }

  /**
   * Registers a callback function to be called when the component is touched.
   *
   * This method sets the `onTouched` callback function.
   *
   * @param {() => void} fn - The callback function to be registered.
   * @returns {void}
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Sets the disabled state of the component.
   *
   * This method sets the `disabled` property of the component based on the provided value.
   *
   * @param {boolean} isDisabled - The disabled state to be set.
   * @returns {void}
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Loads more templates using the continuation token.
   * Called when user scrolls near the bottom of the list.
   */
  loadMore(): void {
    if (this.loading() || !this.continuationToken) return;
    this.fetchTemplates(this.currentSearchTerm, true);
  }

  /**
   * Sets up the search subscription to listen for search input changes.
   * Debounces input and triggers a fresh search when the term changes.
   */
  private setupSearchSubscription(): void {
    this.searchCtrl.valueChanges
      .pipe(
        filter(Boolean),
        tap(() => {
          this.cancelRequest$.next();
          this.isSearchMenuOpen.set(false);
          this.templates.set([]);
          this.continuationToken = null;
          this.hasMore.set(false);
        }),
        debounceTime(300),
        takeUntilDestroyed(),
      )
      .subscribe((search) => {
        this.currentSearchTerm = search;
        this.fetchTemplates(search, false);
      });
  }

  /**
   * Fetches templates from the API.
   *
   * @param search - The search term to filter templates.
   * @param append - If true, appends results to existing templates; otherwise replaces them.
   */
  private fetchTemplates(search: string, append: boolean): void {
    this.loading.set(true);

    const body = this.getBody(search, append ? this.continuationToken : null);

    this.autotagService
      .getTemplates(body)
      .pipe(
        takeUntil(this.cancelRequest$),
        finalize(() => this.loading.set(false)),
        catchError((err) => {
          this.notificationService.showError('Failed to load templates');
          throw err;
        }),
      )
      .subscribe((results) => {
        this.continuationToken = results.continuationToken;
        this.hasMore.set(!!results.continuationToken);

        if (append) {
          this.templates.update((current) => [
            ...current,
            ...results.documents,
          ]);
        } else {
          this.templates.set(results.documents);
        }

        if (this.searchCtrl.value && this.templates().length > 0) {
          this.isSearchMenuOpen.set(true);
        } else {
          this.isSearchMenuOpen.set(false);
        }
      });
  }

  /**
   * Constructs the request body for fetching templates.
   *
   * @param search - The search value to filter templates by.
   * @param continuationToken - Token for pagination, null for first page.
   */
  private getBody(search: string, continuationToken: string | null) {
    const columnFilters = [
      {
        column: 'Template',
        filterModifier: 'AND',
        filters: [{ type: 'Contains', filter: search }],
      },
    ];
    return {
      titleCompany: this.companyId,
      columnFilters,
      continuationToken,
    };
  }

  ngOnDestroy(): void {
    this.cancelRequest$.next();
    this.cancelRequest$.complete();
  }
}
